import { Component, OnInit, signal, computed, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { StockDeliveryRecord, OutboundDeliveryItem } from '../../../stock-delivery.config';
import { StockDeliveryService } from '../../../stock-delivery.service';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { OUTBOUND_DELIVERY_ORDER_PRODUCT_TABLE_COLUMNS } from '../../add/outbound-delivery-order-add.config';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { BASE_PATH, OutboundDeliveryOrderControllerService, ResultRecordControllerService } from '@app/api';
import { environment } from '@environments/environment';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { AdvancedDatepickerComponent, DateRange } from '@shared/components/advanced-datepicker/advanced-datepicker.component';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  formatDeliveryDate,
  getDeliveryStatusSeverity,
  getDeliveryStatusLabel,
  getOutboundTypeLabel,
  formatDeliveryNumber,
} from '../../../delivery.helpers';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { extractErrorMessage } from '@app/shared/utils/error-handler.util';

@Component({
  selector: 'app-confirm-shipping',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ToastModule,
    AppButtonComponent,
    ProgressSpinnerModule,
    TagModule,
    InputTextModule,
    CardModule,
    AdvancedDatepickerComponent,
    DataTableComponent,
  ],
  providers: [
    MessageService,
    {
      provide: BASE_PATH,
      useValue: environment.stockURL,
    },
    OutboundDeliveryOrderControllerService,
    ResultRecordControllerService,
    StockDeliveryService,
  ],
  templateUrl: './confirm-shipping.component.html',
  styleUrl: './confirm-shipping.component.css',
})
export class ConfirmShippingComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private deliveryService = inject(StockDeliveryService);
  private translateService = inject(TranslateService);
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly errorTranslatorService = inject(ErrorTranslatorService);
  private fb = inject(FormBuilder);
  private destroy$ = new Subject<void>();

  // Data signals
  loading = signal(false);
  saving = signal(false);
  private skipCanDeactivate = false;
  formData = signal<StockDeliveryRecord | null>(null);
  productDetails = signal<any[]>([]);
  productTableColumns = signal<TableColumn[]>([]);
  
  // Min date for delivery date picker (today)
  minDeliveryDate = new Date();

  // Computed signal to check if confirm button should be disabled
  // Disable if all pickedQty are 0 or no items
  isConfirmDisabled = computed(() => {
    const details = this.productDetails();
    if (!details || details.length === 0) {
      return true;
    }
    // Check if all pickedQty are 0 or undefined/null
    const allZero = details.every((item: any) => {
      const pickedQty = item.pickedQty;
      return !pickedQty || pickedQty === 0;
    });
    return allZero;
  });

  // Form
  confirmShippingForm!: FormGroup;

  ngOnInit(): void {
    this.initializeForm();
    this.loadViewData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.confirmShippingForm = this.fb.group({
      shippingNo: [''],
      realNoticeCode: [''],
      deliveryDate: [null],
    });
  }

  private loadViewData(): void {
    this.loading.set(true);
    
    const id = this.route.snapshot.paramMap.get('id');
    
    if (!id) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('outbound_management.messages.failed_to_load'),
      });
      this.router.navigate(['/stock/delivery/delivery-order']);
      return;
    }

    // Load basic data
    this.deliveryService.getDeliveryDetail(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: StockDeliveryRecord) => {
          if (data) {
            this.configureForm(data);
            // Load product details separately
            this.loadProductDetails(id);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant('outbound_management.messages.failed_to_load'),
            });
            this.router.navigate(['/stock/delivery/delivery-order']);
            this.loading.set(false);
          }
        },
        error: (error: any) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant('outbound_management.messages.failed_to_load'),
          });
          this.router.navigate(['/stock/delivery/delivery-order']);
          this.loading.set(false);
        }
      });
  }

  private loadProductDetails(id: string): void {
    // Call API to get product details: /v1/outbound/delivery/order/{id}/details
    this.deliveryService.getDeliveryOrderDetails(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items: OutboundDeliveryItem[]) => {
          this.initializeProductDetails(items);
          this.loading.set(false);
        },
        error: (error: any) => {
          // If product details fail, still show basic info but log error
          this.productDetails.set([]);
          this.loading.set(false);
        }
      });
  }

  private configureForm(data: StockDeliveryRecord): void {
    // Format data for display
    const formattedData: StockDeliveryRecord = {
      ...data,
      // Map outTypeMeaning if it's a raw API value, otherwise use as is
      outTypeMeaning: data.outTypeMeaning 
        ? this.getOutboundTypeLabel(data.outTypeMeaning)
        : (data.outType ? this.getOutboundTypeLabel(data.outType) : '-'),
      qty: data.qty,
    };

    this.formData.set(formattedData);

    // Set form values from existing data if available
    const scheduledTime = data.scheduledDeliveryTime;
    if (scheduledTime) {
      const date = typeof scheduledTime === 'string' 
        ? new Date(scheduledTime)
        : scheduledTime;
      if (date instanceof Date && !isNaN(date.getTime())) {
        this.confirmShippingForm.patchValue({
          deliveryDate: date,
        }, { emitEvent: false });
      }
    }
  }

  private initializeProductDetails(detailList: OutboundDeliveryItem[]): void {
    // Build columns: SPU code, SPU name, SKU code, SKU name, plannedQty (before quantity), quantity
    const baseColumns = OUTBOUND_DELIVERY_ORDER_PRODUCT_TABLE_COLUMNS.filter((col: TableColumn) => 
      col.prop !== 'quantity' && col.prop !== 'alreadyOutQty'
    );
    
    const productTableColumns: TableColumn[] = [
      ...baseColumns.map((col: TableColumn) => col),
      // Add plannedQty column before quantity
      {
        label: 'outbound_management.fields.planned_quantity',
        prop: 'plannedQty',
        width: 120,
        type: 'text',
        align: 'right',
      },
      // Add quantity column after plannedQty
      {
        label: 'outbound_management.table.columns.quantity',
        prop: 'quantity',
        width: 120,
        type: 'text',
        align: 'right',
      },
    ];

    // Format detailList with serial numbers
    const formattedDetails = detailList.map((item, index) => ({
      ...item,
      serialNumber: index + 1,
      quantity: typeof item.pickedQty === 'number' ? this.formatNumber(item.pickedQty) : (typeof item.pickedQty !== 'undefined' ? String(item.pickedQty) : '-'),
      plannedQty: typeof item.plannedQty === 'number' ? this.formatNumber(item.plannedQty) : (typeof item.plannedQty !== 'undefined' ? String(item.plannedQty) : '-'),
    }));

    this.productTableColumns.set(productTableColumns);
    this.productDetails.set(formattedDetails);
  }

  // Navigation handlers
  onBackClick(): void {
    this.router.navigate(['/stock/delivery/delivery-order']);
  }

  onConfirmShipping(): void {
    if (this.confirmShippingForm.invalid) {
      this.confirmShippingForm.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('common.messages.required_fields_missing'),
      });
      return;
    }

    const order = this.formData();
    if (!order) {
      return;
    }

    this.saving.set(true);

    const formValue = this.confirmShippingForm.getRawValue();
    const deliveryDate = formValue.deliveryDate 
      ? (formValue.deliveryDate instanceof Date 
          ? formValue.deliveryDate.toISOString() 
          : new Date(formValue.deliveryDate).toISOString())
      : undefined;

    const realNoticeCode = formValue.realNoticeCode?.trim() || undefined;

    this.deliveryService
      .confirmShipping(order.id, realNoticeCode, deliveryDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.skipCanDeactivate = true;
          this.saving.set(false);
          this.confirmShippingForm?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant('outbound_management.messages.shipped_successfully'),
          });
          this.router.navigate(['/stock/delivery/delivery-order']);
        },
        error: (error) => {
          this.saving.set(false);
          // RBA-3132: always surface API 409 CONCURRENT_MODIFICATION to the failed session
          this.showConfirmShippingError(error);
        },
      });
  }

  private showConfirmShippingError(error: unknown): void {
    const rawMessage = extractErrorMessage(error);
    const fallbackKey = 'outbound_management.messages.failed_to_confirm_shipping';
    const translated = this.errorTranslatorService.translate(
      rawMessage,
      fallbackKey,
    );
    const fallback = this.translateService.instant(fallbackKey);
    // If pattern didn't match but BE still sent a concurrent-modification message, show it.
    const detail =
      translated === fallback && /concurrent modification/i.test(rawMessage || '')
        ? rawMessage
        : translated;
    this.errorDialogService.showError(detail);
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.saving() || this.loading()) {
      return true;
    }

    return !this.confirmShippingForm?.dirty;
  }

  onDeliveryDateChange(date: Date | DateRange | null): void {
    if (date instanceof Date) {
      this.confirmShippingForm.patchValue({ deliveryDate: date }, { emitEvent: false });
    } else if (date === null) {
      this.confirmShippingForm.patchValue({ deliveryDate: null }, { emitEvent: false });
    }
  }


  // Helper methods for template
  getFieldValue(fieldName: string): any {
    const data = this.formData();
    if (!data) return null;
    return (data as any)[fieldName] || null;
  }

  getWarehouseDisplay(codeField: string, nameField: string): string {
    const code = this.getFieldValue(codeField);
    const name = this.getFieldValue(nameField);
    
    if (code && name) {
      return `${code} - ${name}`;
    } else if (code) {
      return code;
    } else if (name) {
      return name;
    }
    return '-';
  }

  formatDate = formatDeliveryDate;

  getStatusSeverity = getDeliveryStatusSeverity;

  private getOutboundTypeLabel(outTypeMeaning: string): string {
    return getOutboundTypeLabel(outTypeMeaning, this.translateService);
  }

  getStatusLabel(status: string): string {
    const outType = this.getFieldValue('outType');
    return getDeliveryStatusLabel(status, this.translateService, false, outType);
  }

  private formatNumber = formatDeliveryNumber;
}

